import { Navigate, Outlet } from 'react-router';
import { usePermissions } from '@/hooks/use-permissions';

type MatchMode = 'prefix' | 'exact';

interface PermissionRouteProps {
  /**
   * Código de permissão a verificar (ex.: `ControlCodes.admin` para prefix,
   * `ControlCodes.admin_user_create` para exact).
   */
  require: string;
  /**
   * Modo de verificação:
   * - `prefix` (default): libera se o usuário tiver `require` ou qualquer
   *   permissão filha iniciada por `require:`. Use para módulos/submódulos
   *   hierárquicos (ex.: área de usuários, lista de devices).
   * - `exact`: exige o code folha exato. Use para ações atômicas
   *   (create/edit/delete/etc.) ou rotas que representem uma funcionalidade
   *   específica sem filhos.
   */
  match?: MatchMode;
  /**
   * Rota de fallback quando o usuário não tem permissão. Default `/home`
   * (sempre acessível a usuários autenticados).
   */
  fallback?: string;
}

/**
 * Guard de rota por permissão.
 *
 * Uso como layout route envolvendo um grupo de rotas filhas:
 *
 * ```tsx
 * // Prefix — módulo/submódulo hierárquico
 * <Route element={<PermissionRoute require={ControlCodes.admin} />}>
 *   ...
 * </Route>
 *
 * // Exact — ação atômica
 * <Route element={<PermissionRoute require={ControlCodes.admin_user_create} match="exact" />}>
 *   ...
 * </Route>
 * ```
 *
 * Observação: defesa em profundidade — o backend permanece a fonte final de
 * autorização. Este guard apenas evita renderização de páginas/módulos não
 * autorizados via acesso direto por URL.
 */
export function PermissionRoute({
  require,
  match = 'prefix',
  fallback = '/home',
}: PermissionRouteProps) {
  const { hasPermission, hasPermissionPrefix } = usePermissions();

  const allowed = match === 'exact' ? hasPermission(require) : hasPermissionPrefix(require);

  if (!allowed) {
    return <Navigate to={fallback} replace />;
  }

  return <Outlet />;
}
